import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { ESTADO_NAMES, humanize, STATUS_EXPLANATIONS } from '../lib/filters';
import type { CrimeMunicipalSummary, GradoRezago, PropertyDetail as PD } from '../lib/types';
import { openFicha } from '../lib/ficha';
import { oppTier } from '../lib/opportunity';
import { EmptyState } from './EmptyState';
import { gradoToColor } from './SocioLayer';
import { PropertyDetailSkeleton } from './Skeletons';
import { useAuth } from '../lib/auth';
import { openLogin, openPlanPanel } from '../lib/signup';
import { RenewalRequest } from './RenewalRequest';

interface Props {
  propertyId: string | null;
  onClose: () => void;
}

/**
 * Drawer derecho con la ficha completa de una property — TK-S02-01.
 *
 * Diseño:
 *   - Panel fijo a la derecha (380px desktop, 100% ancho < 768px).
 *   - Cierra con: X, click en overlay, o tecla Esc.
 *   - Secciones colapsables visualmente (header → foto → grupos):
 *       1. Header (precio + descuento + tipo + canal/status)
 *       2. Galería de fotos (si hay)
 *       3. Identificación (ID interno, dirección, coords)
 *       4. Localización (estado, municipio, colonia, CP, AGEB)
 *       5. Económico (precio, avalúo, descuento, predial)
 *       6. Características (terreno, construcción, recámaras, baños, etc.)
 *       7. Estado legal (hipoteca, gravamen, embargo, ejido, riesgos)
 *       8. Trazabilidad (fechas + fuentes + link a portal)
 *
 * Principio: TODOS los campos del backlog TK-S02-01 son siempre
 * visibles. Cuando un dato falta, mostramos "—" (no omitimos el campo).
 * Esto permite al usuario distinguir "dato no disponible" de "campo
 * que no existe en la ficha".
 */
export function PropertyDetail({ propertyId, onClose }: Props) {
  const [data, setData] = useState<PD | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [copyState, setCopyState] = useState<'idle' | 'ok'>('idle');
  const [crime, setCrime] = useState<CrimeMunicipalSummary | null>(null);
  const [crimeLoading, setCrimeLoading] = useState(false);
  /** El fallo fue "no tienes sesión", no un error real de carga. */
  const [needsAuth, setNeedsAuth] = useState(false);
  /**
   * Hay sesión pero el plan no deja abrir la ficha (RFC-0025): cupo del
   * mes agotado, plan vencido, organización suspendida. Antes cualquier
   * 403 se mostraba como "inicia sesión" a alguien que YA tenía sesión.
   */
  const [blocked, setBlocked] = useState<{ code: string; body: Record<string, unknown> } | null>(null);
  const drawerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!propertyId) { setData(null); return; }
    setLoading(true);
    setError(null);
    setNeedsAuth(false);
    setBlocked(null);
    setData(null);
    setCrime(null);
    setPhotoIdx(0);
    setCopyState('idle');
    const ctrl = new AbortController();
    api.propertyDetail(propertyId, ctrl.signal)
      // El detalle viene de una promesa cacheada/compartida que el abort no
      // cancela — el guard en then evita que la respuesta de una propiedad
      // ANTERIOR pise a la actual al cambiar rápido de selección.
      .then((d) => {
        if (ctrl.signal.aborted) return;
        setData(d); setLoading(false);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setNeedsAuth(e instanceof ApiError && e.status === 401);
        if (e instanceof ApiError && e.status === 403) setBlocked({ code: e.code, body: e.body });
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [propertyId]);

  // Crime se carga en una segunda request paralela al detalle. Si no
  // hay cve_mun (propiedad sin geocoder) o el dataset no está cargado
  // (404/503 → null silencioso), simplemente no se muestra la sección.
  useEffect(() => {
    if (!data?.cve_mun) { setCrime(null); return; }
    setCrimeLoading(true);
    const ctrl = new AbortController();
    api.crime(data.cve_mun, ctrl.signal)
      .then((r) => { setCrime(r?.summary ?? null); setCrimeLoading(false); })
      .catch(() => { setCrime(null); setCrimeLoading(false); });
    return () => ctrl.abort();
  }, [data?.cve_mun]);

  // Gestión de foco del diálogo: al abrir, el foco entra al drawer (los
  // usuarios de teclado tabulaban al mapa de atrás); al cerrar, regresa
  // al elemento que lo tenía (la card o el marker que abrió el detalle).
  useEffect(() => {
    if (!propertyId) return;
    const previous = document.activeElement as HTMLElement | null;
    // El drawer se monta en este mismo commit — diferimos un tick.
    const t = window.setTimeout(() => drawerRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(t);
      previous?.focus?.();
    };
  }, [propertyId]);

  // Esc cierra cuando hay drawer abierto.
  useEffect(() => {
    if (!propertyId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [propertyId, onClose]);

  const coords = useMemo(() => parseCoords(data?.coords_geojson ?? null), [data?.coords_geojson]);

  if (!propertyId) return null;

  const photos = data?.photo_urls ?? [];
  const currentPhoto = photos[Math.min(photoIdx, photos.length - 1)];

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Detalle de propiedad"
        tabIndex={-1}
        ref={drawerRef}
      >
        <button className="drawer-close" onClick={onClose} aria-label="Cerrar detalle">×</button>

        {loading && !data && <PropertyDetailSkeleton />}
        {error && (
          <div className="drawer-error">
            {blocked ? (
              <BlockedState code={blocked.code} body={blocked.body} onClose={onClose} />
            ) : needsAuth ? (
              // Deep-link a una ficha sin sesión (RFC-0021 Bloque C): no es
              // un fallo, es la frontera — se explica y se ofrece entrar.
              <EmptyState
                icon="🔒"
                title="Esta ficha requiere una cuenta"
                hint="El detalle del inmueble, su ubicación y su historia son para cuentas de TerraKairos. Las cuentas las abre TerraKairos por acuerdo."
                actionLabel="Iniciar sesión"
                onAction={openLogin}
              />
            ) : (
              <EmptyState
                variant="error"
                icon="!"
                title="No pudimos cargar la propiedad"
                hint={error}
                actionLabel="Cerrar"
                onAction={onClose}
              />
            )}
          </div>
        )}

        {data && (
          <>
            {/* ── Header: precio + descuento + tags ─────────────── */}
            <header className="drawer-header">
              <div className="drawer-price">
                {data.asking_price !== null && data.asking_price > 1
                  ? fmtMoney(data.asking_price, data.currency)
                  : 'Sin precio publicado'}
                {data.discount_pct !== null && data.discount_pct > 0 && (
                  <span className="drawer-discount">-{Number(data.discount_pct).toFixed(0)}%</span>
                )}
              </div>
              <div className="drawer-address" title={data.address_normalized ?? data.address_raw ?? undefined}>
                {data.address_normalized ?? data.address_raw ?? 'Sin dirección'}
              </div>
              <div className="drawer-meta">
                {humanize('propertyType', data.property_type)}
                {data.cve_ent && ' · ' + (ESTADO_NAMES[data.cve_ent] ?? data.cve_ent)}
                {data.municipio && ' · ' + data.municipio}
              </div>
              <div className="drawer-tags">
                <Tag>{humanize('channel', data.legal_channel)}</Tag>
                <Tag accent title={STATUS_EXPLANATIONS[data.acquisition_status]}>
                  {humanize('status', data.acquisition_status)}
                </Tag>
                {data.opportunity_score !== null && data.opportunity_score !== undefined && (
                  <OpportunityHeaderBadge score={data.opportunity_score} />
                )}
                {data.opp_has_litigio === true && (
                  <span className="flag flag-warn" title="Litigio pendiente reportado en el listing">
                    ⚠ Litigio pendiente
                  </span>
                )}
                {data.opp_is_libre_gravamen === true && (
                  <span className="flag flag-good" title="Listing declara libre de gravamen">
                    ✓ Libre de gravamen
                  </span>
                )}
                {data.opp_discount_vs_avaluo_pct !== null
                  && data.opp_discount_vs_avaluo_pct !== undefined
                  && data.opp_discount_vs_avaluo_pct >= 25 && (
                  <span
                    className="flag flag-joya"
                    title={`Asking ${data.opp_discount_vs_avaluo_pct.toFixed(1)}% bajo el avalúo`}
                  >
                    ★ {data.opp_discount_vs_avaluo_pct.toFixed(0)}% bajo avalúo
                  </span>
                )}
                {data.score_overall !== null && (
                  <Tag>Score legacy {Number(data.score_overall).toFixed(1)}</Tag>
                )}
              </div>
              <div className="drawer-actions">
                <WatchControls propertyId={data.id} />
                <button
                  type="button"
                  className="ficha-btn"
                  onClick={() => openFicha(data, { coords })}
                  title="Abre la ficha técnica en una pestaña nueva, lista para imprimir o guardar como PDF"
                >
                  ⎙ Ficha técnica (PDF)
                </button>
              </div>
            </header>

            {/* ── Galería ───────────────────────────────────────── */}
            {photos.length > 0 && currentPhoto && (
              <div className="drawer-gallery">
                <img
                  className="drawer-photo"
                  src={currentPhoto}
                  alt="Foto de la propiedad"
                  loading="lazy"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                {photos.length > 1 && (
                  <div className="drawer-photo-nav">
                    <button aria-label="Foto anterior" onClick={() => setPhotoIdx((i) => Math.max(0, i - 1))} disabled={photoIdx === 0}>‹</button>
                    <span>{photoIdx + 1} / {photos.length}</span>
                    <button aria-label="Foto siguiente" onClick={() => setPhotoIdx((i) => Math.min(photos.length - 1, i + 1))} disabled={photoIdx === photos.length - 1}>›</button>
                  </div>
                )}
              </div>
            )}

            {/* ── 1. Identificación ─────────────────────────────── */}
            <Section title="Identificación">
              <Attr label="ID TerraKairos">
                <CopyChip text={data.id} short={shortId(data.id)} onCopied={() => setCopyState('ok')} state={copyState} setState={setCopyState} />
              </Attr>
              <Attr label="Dirección">{dash(data.address_normalized ?? data.address_raw)}</Attr>
              <Attr label="Coordenadas">
                {coords ? (
                  <CopyChip
                    text={`${coords.lat}, ${coords.lng}`}
                    short={`${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`}
                    onCopied={() => setCopyState('ok')}
                    state={copyState}
                    setState={setCopyState}
                  />
                ) : '—'}
              </Attr>
              <Attr label="Folio real">{dash(data.folio_real)}</Attr>
              <Attr label="Clave catastral">{dash(data.clave_catastral)}</Attr>
            </Section>

            {/* ── 2. Localización ───────────────────────────────── */}
            <Section title="Localización">
              <Attr label="Estado">{dash(data.cve_ent ? ESTADO_NAMES[data.cve_ent] ?? data.cve_ent : null)}</Attr>
              <Attr label="Municipio">{dash(data.municipio)}</Attr>
              <Attr label="Colonia">{dash(data.colonia)}</Attr>
              <Attr label="Código postal">{dash(data.codigo_postal)}</Attr>
              <Attr label="CVE municipio">{dash(data.cve_mun)}</Attr>
              <Attr label="CVE AGEB">{dash(data.cve_ageb)}</Attr>
            </Section>

            {/* ── Opportunity Score breakdown ───────────────────── */}
            {data.opportunity_score !== null && data.opportunity_score !== undefined && (
              <Section title="Opportunity Score (TerraKairos)">
                <OpportunityPanel data={data} breakdownLocked={data.locked?.includes('score_breakdown') ?? false} />
              </Section>
            )}

            {/* ── Seguridad y entorno (SESNSP) ──────────────────── */}
            {/* Solo se renderiza si hay cve_mun (geocoded). El loader es
                silencioso: 404/503 → null, no muestra sección. Footer
                aclara metodología y fuente para que el cliente decida
                cuánto peso darle al dato. */}
            {data.cve_mun && (crimeLoading || crime) && (
              <Section title="Seguridad y entorno (SESNSP)">
                {crimeLoading && <div className="empty">cargando incidencia delictiva…</div>}
                {crime && <CrimePanel crime={crime} />}
              </Section>
            )}

            {/* ── 3. Económico ──────────────────────────────────── */}
            <Section title="Económico">
              <Attr label="Precio listado">
                {data.asking_price !== null && data.asking_price > 1
                  ? fmtMoney(data.asking_price, data.currency)
                  : '—'}
              </Attr>
              <Attr label="Avalúo">
                {data.avaluo_price !== null && data.avaluo_price > 1
                  ? fmtMoney(data.avaluo_price, data.currency)
                  : '—'}
              </Attr>
              <Attr label="Descuento">
                {data.discount_pct !== null && data.discount_pct > 0
                  ? `${Number(data.discount_pct).toFixed(1)}%`
                  : '—'}
              </Attr>
              <Attr label="Descuento vs avalúo">
                {data.opp_discount_vs_avaluo_pct !== null
                  && data.opp_discount_vs_avaluo_pct !== undefined
                  && data.opp_discount_vs_avaluo_pct > 0
                  ? <span className={data.opp_discount_vs_avaluo_pct >= 25 ? 'discount-strong' : ''}>
                      {data.opp_discount_vs_avaluo_pct.toFixed(1)}%
                      {data.opp_discount_vs_avaluo_pct >= 25 && ' (joya)'}
                    </span>
                  : '—'}
              </Attr>
              <Attr label="Adeudo predial">
                {data.predial_balance !== null && data.predial_balance > 0
                  ? fmtMoney(data.predial_balance, data.currency)
                  : '—'}
              </Attr>
              <Attr label="Postura mínima">
                {data.minimum_bid !== null && data.minimum_bid > 0
                  ? fmtMoney(data.minimum_bid, data.currency)
                  : '—'}
              </Attr>
              <Attr label="Moneda">{dash(data.currency)}</Attr>
            </Section>

            {/* ── 3b. Historial de precio (RFC-0019) ─────────────── */}
            {data.locked?.includes('price_history') ? (
              <Section title="Historial de precio">
                <LockedNote what="El historial de cambios de precio" />
              </Section>
            ) : hasPriceChanges(data) && (
              <Section title="Historial de precio">
                <PriceHistoryList
                  history={data.price_history ?? []}
                  currency={data.currency}
                />
              </Section>
            )}

            {data.quota && data.quota.limit !== null && (
              <p className="quota-footnote">
                {data.quota.repeat
                  ? <>Ya la abriste este mes: no descuenta. Llevas {data.quota.used.toLocaleString('es-MX')} de {data.quota.limit.toLocaleString('es-MX')} fichas.</>
                  : <>Ficha {data.quota.used.toLocaleString('es-MX')} de {data.quota.limit.toLocaleString('es-MX')} de este mes en tu plan. Volver a abrirla este mes no descuenta.</>}
              </p>
            )}

            {/* ── 4. Características ────────────────────────────── */}
            <Section title="Características">
              <Attr label="Tipo">{humanize('propertyType', data.property_type)}</Attr>
              <Attr label="Construcción">
                {data.surface_built_m2 !== null
                  ? `${Number(data.surface_built_m2).toLocaleString('es-MX')} m²`
                  : '—'}
              </Attr>
              <Attr label="Terreno">
                {data.surface_terrain_m2 !== null
                  ? `${Number(data.surface_terrain_m2).toLocaleString('es-MX')} m²`
                  : '—'}
              </Attr>
              <Attr label="Recámaras">{dash(data.bedrooms)}</Attr>
              <Attr label="Baños">{dash(data.bathrooms)}</Attr>
              <Attr label="Estacionamiento">{dash(data.parking)}</Attr>
              <Attr label="Año">{dash(data.year_built)}</Attr>
              <Attr label="Condición">{dash(data.condition !== 'DESCONOCIDO' ? data.condition : null)}</Attr>
            </Section>

            {/* ── Riesgos físicos CENAPRED ─────────────────────── */}
            {(data.cenapred_seismic_zone || data.cenapred_flood_risk) && (
              <Section title="Riesgos físicos (CENAPRED)">
                <Attr label="Zona sísmica (NTC)">
                  {data.cenapred_seismic_zone
                    ? `Zona ${data.cenapred_seismic_zone}`
                    : '—'}
                </Attr>
                <Attr label="Riesgo de inundación">
                  {data.cenapred_flood_risk ?? '—'}
                </Attr>
                <p className="section-footnote">
                  Fuente: Atlas Nacional de Riesgos (CENAPRED). Sísmico por ubicación;
                  inundación por municipio INEGI.
                </p>
              </Section>
            )}

            {/* ── Tendencia de mercado SHF ─────────────────────── */}
            {data.shf_yoy_pct !== null && data.shf_yoy_pct !== undefined && (
              <Section title="Tendencia de mercado (SHF)">
                <Attr label="Plusvalía interanual">
                  <span className={data.shf_yoy_pct >= 0 ? 'shf-up' : 'shf-down'}>
                    {data.shf_yoy_pct >= 0 ? '▲' : '▼'} {data.shf_yoy_pct.toFixed(1)}%
                  </span>
                </Attr>
                {data.shf_appreciation_5y_pct !== null && data.shf_appreciation_5y_pct !== undefined && (
                  <Attr label="Apreciación 5 años">
                    <span className={data.shf_appreciation_5y_pct >= 0 ? 'shf-up' : 'shf-down'}>
                      {data.shf_appreciation_5y_pct >= 0 ? '+' : ''}{data.shf_appreciation_5y_pct.toFixed(1)}%
                    </span>
                  </Attr>
                )}
                <Attr label="Índice (base 2017=100)">
                  {data.shf_index !== null && data.shf_index !== undefined
                    ? `${data.shf_index.toFixed(1)}${data.shf_anio ? ` · ${data.shf_anio}-T${data.shf_trimestre ?? ''}` : ''}`
                    : '—'}
                </Attr>
                <Attr label="Cobertura del dato">
                  {data.shf_level === 'municipio' ? 'Municipal' : data.shf_level === 'estado' ? 'Estatal' : 'Nacional'}
                </Attr>
                <p className="section-footnote">
                  Fuente: SHF, Índice de Precios de la Vivienda (datos abiertos). Es un
                  índice de apreciación de la zona —no un avalúo individual— útil como
                  señal de plusvalía/tendencia del mercado local.
                </p>
              </Section>
            )}

            {/* ── Nivel socioeconómico CONEVAL (RFC-0022 Fase 2) ── */}
            <Section title="Nivel socioeconómico (CONEVAL)">
              {data.socio_grado ? (
                <>
                  <Attr label="Nivel">
                    <span className="socio-grado" style={{ color: gradoToColor(data.socio_grado) }}>
                      ◉ {NIVEL_SOCIO[data.socio_grado]}
                    </span>
                    <span className="socio-grado-raw"> · rezago social {data.socio_grado.toLowerCase()}</span>
                  </Attr>
                  <Attr label="Escolaridad promedio">{numDash(data.socio_graproes, ' años')}</Attr>
                  <Attr label="Viviendas con internet">{numDash(data.socio_pct_internet, '%')}</Attr>
                  <Attr label="Viviendas con automóvil">{numDash(data.socio_pct_automovil, '%')}</Attr>
                  <Attr label="Viviendas con piso de tierra">{numDash(data.socio_pct_piso_tierra, '%')}</Attr>
                  <Attr label="Precisión del dato">
                    {data.geocode_precision === 'STREET' ? 'AGEB de la dirección' : 'AGEB de la colonia'}
                  </Attr>
                  <p className="section-footnote">
                    Grado de Rezago Social CONEVAL 2020 + Censo INEGI 2020 de la AGEB urbana donde cae
                    la ubicación. La escala va invertida: rezago «muy bajo» = nivel alto. Describe la
                    zona, no el inmueble.
                  </p>
                </>
              ) : (
                <p className="section-footnote">
                  {!data.geocode_precision || data.geocode_precision === 'MUNICIPAL_CENTROID' || data.geocode_precision === 'NONE'
                    ? 'Sin dato: la ubicación sólo se conoce a nivel municipio y el nivel de la AGEB del centro no representa al inmueble. Se completa cuando la geocodificación llegue a colonia.'
                    : 'Sin dato: la AGEB donde cae la ubicación no tiene grado publicado por CONEVAL (AGEB rural o sin censo).'}
                </p>
              )}
            </Section>

            {/* ── 5. Estado legal ───────────────────────────────── */}
            <Section title="Estado legal">
              <Attr label="Canal">{humanize('channel', data.legal_channel)}</Attr>
              <Attr label="Estatus" title={STATUS_EXPLANATIONS[data.acquisition_status]}>
                {humanize('status', data.acquisition_status)}
              </Attr>
              <Attr label="Hipoteca vigente">{boolDash(data.hipoteca_vigente)}</Attr>
              <Attr label="Libre de gravamen">{boolDash(data.libre_de_gravamen)}</Attr>
              <Attr label="Embargo activo">{boolDash(data.embargo_activo)}</Attr>
              <Attr label="Litigio pendiente">{boolDash(data.litigio_pendiente)}</Attr>
              <Attr label="Ejido">{boolDash(data.ejido_flag)}</Attr>
              <Attr label="Zona de riesgo">{boolDash(data.zona_riesgo_flag)}</Attr>
              <Attr label="ISR trap">{boolDash(data.isr_trap_risk)}</Attr>
              {(data.auction_start_date || data.auction_end_date) && (
                <Attr label="Subasta">
                  {fmtDate(data.auction_start_date) ?? '—'} → {fmtDate(data.auction_end_date) ?? '—'}
                </Attr>
              )}
            </Section>

            {/* ── 6. Trazabilidad ───────────────────────────────── */}
            <Section title={`Fuentes (${data.sources?.length ?? 0})`}>
              {data.sources && data.sources.length > 0 ? (
                data.sources.map((s) => (
                  <div key={s.sourceId} className="drawer-source">
                    <div className="src-line">
                      <strong>{s.sourceType}</strong>
                      <span className="src-date">{fmtDate(s.fetchedAt) ?? '—'}</span>
                    </div>
                    <div className="src-id">id: {s.sourceId}</div>
                    {s.sourceUrl ? (
                      <a href={s.sourceUrl} target="_blank" rel="noopener noreferrer" className="src-url">
                        Ver en portal original ↗
                      </a>
                    ) : (
                      <span className="src-url-missing">Sin link al portal</span>
                    )}
                  </div>
                ))
              ) : (
                <div className="src-empty">—</div>
              )}
              <div className="src-meta">
                <Attr label="Detectado">{fmtDate(data.first_seen_at) ?? '—'}</Attr>
                <Attr label="Actualizado">{fmtDate(data.last_updated_at) ?? '—'}</Attr>
                {data.delisted_at && (
                  <Attr label="Delisted">{fmtDate(data.delisted_at) ?? '—'}</Attr>
                )}
              </div>
            </Section>
          </>
        )}
      </aside>
    </>
  );
}

// ─── Subcomponentes ──────────────────────────────────────────────

/**
 * Panel de incidencia delictiva por municipio. Muestra los 5 delitos
 * que más impactan decisiones de inversión inmobiliaria (homicidio
 * doloso, robo a casa, robo de vehículo, extorsión, secuestro), más el
 * total para contexto.
 *
 * Tasa por 10k habitantes NO se calcula acá — el dato del SESNSP es
 * eventos absolutos. Una versión futura cruzará con población INEGI;
 * por ahora la presentación es honesta: "X eventos en Y meses".
 */
function CrimePanel({ crime }: { crime: CrimeMunicipalSummary }) {
  // Cada categoría: valor 12m + delta YoY % (vs 12m anteriores). El
  // delta puede ser null si prev == 0 (no comparable). Threshold de
  // "danger" sobre el valor absoluto se mantiene como semáforo rápido.
  const items: Array<{
    label: string;
    value: number;
    yoy: number | null;
    danger: boolean;
  }> = [
    { label: 'Homicidio doloso', value: crime.homicidio_doloso_12m, yoy: crime.homicidio_doloso_yoy_pct, danger: crime.homicidio_doloso_12m >= 10 },
    { label: 'Robo a casa habitación', value: crime.robo_casa_12m, yoy: crime.robo_casa_yoy_pct, danger: crime.robo_casa_12m >= 50 },
    { label: 'Robo de vehículo', value: crime.robo_vehiculo_12m, yoy: crime.robo_vehiculo_yoy_pct, danger: crime.robo_vehiculo_12m >= 100 },
    { label: 'Extorsión', value: crime.extorsion_12m, yoy: crime.extorsion_yoy_pct, danger: crime.extorsion_12m >= 5 },
    { label: 'Secuestro', value: crime.secuestro_12m, yoy: crime.secuestro_yoy_pct, danger: crime.secuestro_12m >= 1 },
  ];
  const period = formatCrimePeriod(crime.yyyymm_min, crime.yyyymm_max);
  // Promedio anual de los 36m previos como contexto del nivel "normal"
  const avg36mAnual = Math.round(crime.total_delitos_36m / 3);
  return (
    <>
      <dl className="drawer-attrs">
        <Attr label="Total eventos (12m)">
          <strong>{crime.total_delitos_12m.toLocaleString('es-MX')}</strong>
          <YoyBadge value={crime.total_delitos_yoy_pct} />
        </Attr>
        {period && <Attr label="Periodo">{period}</Attr>}
        <Attr label="Promedio anual (36m)" title="Promedio de eventos por año en los últimos 36 meses. Sirve como baseline para comparar el periodo current.">
          <span className="crime-baseline">{avg36mAnual.toLocaleString('es-MX')}</span>
        </Attr>
        {items.map((it) => (
          <Attr key={it.label} label={it.label}>
            <span className={it.danger ? 'crime-danger' : 'crime-ok'}>
              {it.value.toLocaleString('es-MX')}
            </span>
            <YoyBadge value={it.yoy} />
          </Attr>
        ))}
      </dl>
      <div className="crime-footer" title="Eventos absolutos reportados al SESNSP por la fiscalía estatal. NO normalizado por población. YoY = comparación contra los 12 meses anteriores al periodo current. Útil como semáforo direccional, no como índice oficial.">
        Fuente: SESNSP — incidencia delictiva municipal. Comparativo YoY vs 12 meses previos.
      </div>
    </>
  );
}

/**
 * Badge visual de tendencia YoY. Convención de color:
 *   - Bajó (< 0): verde (mejora)
 *   - Subió (> 0): rojo (empeora)
 *   - 0 o null: gris (sin cambio o no comparable)
 *
 * Para crímenes "menos es mejor", el sentido es directo. Si en el futuro
 * agregamos métricas tipo "esclarecimiento" donde "más es mejor", debe
 * pasarse un prop `inverted` para invertir colores.
 */
function YoyBadge({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return null;
  if (value === 0) {
    return <span className="yoy-badge yoy-flat" title="Sin cambio vs año anterior">·</span>;
  }
  const isUp = value > 0;
  const arrow = isUp ? '▲' : '▼';
  const cls = isUp ? 'yoy-badge yoy-up' : 'yoy-badge yoy-down';
  const formatted = `${isUp ? '+' : ''}${value.toFixed(1)}%`;
  return (
    <span className={cls} title={`${formatted} vs los 12 meses anteriores`}>
      {arrow} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

/**
 * Badge grande para el header del drawer: el score y su banda de color.
 * Sin adjetivo («Excelente», «Bueno»…): desde el 2026-10-08 la ficha no
 * califica, describe. Tooltip explica metodología en una línea.
 */
function OpportunityHeaderBadge({ score }: { score: number }) {
  const rounded = Math.round(score);
  const cls = `opp-tag opp-${oppTier(rounded)}`;
  return (
    <span
      className={cls}
      title="Opportunity Score 0-100 — combina precio vs zona (28%), seguridad YoY (23%), completitud del listing (18%), claridad legal (14%), liquidez del tipo (7%) y riesgo físico CENAPRED (10%)"
    >
      Opportunity {rounded}/100
    </span>
  );
}

/**
 * Panel detallado del Opportunity Score con breakdown de los 5 factores
 * que lo componen + métricas comparativas (precio vs zona, crime YoY).
 * Diseñado para que el inversor entienda EL POR QUÉ del score, no solo
 * el número.
 */
interface OpportunityPanelData {
  opportunity_score: number | null;
  opp_factor_price: number | null;
  opp_factor_safety: number | null;
  opp_factor_completeness: number | null;
  opp_factor_legal: number | null;
  opp_factor_liquidity: number | null;
  opp_factor_physical: number | null;
  opp_factor_socio?: number | null;
  opp_price_m2: number | null;
  opp_price_m2_zscore: number | null;
  opp_zone_avg_price_m2: number | null;
  opp_zone_median_price_m2: number | null;
  opp_zone_n_comparables: number | null;
  opp_crime_yoy_pct: number | null;
  opp_homicidio_yoy_pct: number | null;
  // Señales v2 (MV 0015) — opcionales para back compat
  opp_discount_vs_avaluo_pct?: number | null;
  opp_n_photos?: number | null;
  opp_has_litigio?: boolean | null;
  opp_is_libre_gravamen?: boolean | null;
}

/** Aviso en lugar de un dato que el plan no incluye (RFC-0025). */
function LockedNote({ what }: { what: string }) {
  return (
    <p className="locked-note">
      🔒 {what} no está incluido en tu plan actual.{' '}
      <button type="button" className="auth-link" onClick={openPlanPanel}>Ver planes</button>
    </p>
  );
}

/** Ficha bloqueada por el plan: cupo del mes, vigencia o suspensión. */
function BlockedState({ code, body, onClose }: { code: string; body: Record<string, unknown>; onClose: () => void }) {
  const { user } = useAuth();
  const email = user?.email ?? '';
  if (code === 'quota_exceeded') {
    const used = Number(body.used ?? 0);
    const limit = Number(body.limit ?? 0);
    return (
      <EmptyState
        icon="⏳"
        title="Llegaste al tope de fichas de tu plan este mes"
        hint={`Tu organización abrió ${used.toLocaleString('es-MX')} de ${limit.toLocaleString('es-MX')} fichas incluidas este mes. El cupo se reinicia el día 1; el mapa y la lista siguen disponibles.`}
        actionLabel="Ver planes"
        onAction={openPlanPanel}
        secondaryLabel="Cerrar"
        onSecondary={onClose}
      />
    );
  }
  const copy: Record<string, [string, string]> = {
    plan_expired: ['El plan de tu organización venció', 'Tus datos y observadas se conservan. Pide la renovación y te contactamos.'],
    org_suspended: ['La cuenta de tu organización está suspendida', 'Pide que la reactiven y te contactamos.'],
    password_change_required: ['Define tu contraseña para continuar', 'Tu cuenta usa una contraseña temporal. Cámbiala desde el menú de tu cuenta.'],
  };
  const [title, hint] = copy[code] ?? ['No tienes acceso a esta ficha', 'Tu plan no incluye este contenido.'];
  if (code === 'plan_expired' || code === 'org_suspended') {
    return (
      <EmptyState icon="🔒" title={title} hint={hint} secondaryLabel="Cerrar" onSecondary={onClose}>
        {email && <div className="es-renewal"><RenewalRequest email={email} reason={code} /></div>}
      </EmptyState>
    );
  }
  return <EmptyState icon="🔒" title={title} hint={hint} actionLabel="Cerrar" onAction={onClose} />;
}

function OpportunityPanel({ data, breakdownLocked = false }: { data: OpportunityPanelData; breakdownLocked?: boolean }) {
  const factors: Array<{
    label: string;
    value: number | null;
    weight: number;
    hint: string;
  }> = [
    // Pesos = migración 0030 (RFC-0022 Fase 2b). Si cambian allá, cambian aquí.
    { label: 'Precio vs zona', value: data.opp_factor_price, weight: 27, hint: 'z-score del $/m² comparado con la media municipal del mismo tipo. <0 = barato, >0 = caro.' },
    { label: 'Seguridad', value: data.opp_factor_safety, weight: 21, hint: 'Basado en tendencia YoY de delitos totales del municipio (SESNSP). 50 = neutral cuando no hay datos.' },
    { label: 'Completitud', value: data.opp_factor_completeness, weight: 15, hint: 'Cuántos campos del listing están poblados (fotos, coords, avalúo, surface, etc).' },
    { label: 'Legal', value: data.opp_factor_legal, weight: 14, hint: 'Claridad jurídica: adjudicado banco > SHF > INDEP > judicial. Penaliza ejido, zona restringida, litigio.' },
    { label: 'Liquidez', value: data.opp_factor_liquidity, weight: 6, hint: 'Facilidad de reventa según tipo de propiedad. CASA/DEPTO altos; TERRENO/RANCHO bajos.' },
    { label: 'Riesgo físico', value: data.opp_factor_physical, weight: 7, hint: 'CENAPRED: zona sísmica A–D + grado de inundación municipal. Peor de ambos factores.' },
    { label: 'Nivel socioeconómico', value: data.opp_factor_socio ?? null, weight: 10, hint: 'Grado de rezago social CONEVAL de la AGEB (nivel alto = 100 … bajo = 0). Sólo con ubicación a nivel colonia o calle; sin dato, su peso se reparte entre los demás factores en vez de contar como neutral.' },
  ];
  return (
    <>
      {breakdownLocked ? <LockedNote what="El desglose del score por factor y la comparación con la zona" /> : (
      <div className="opp-bars">
        {factors.map((f) => (
          <div className="opp-bar-row" key={f.label} title={f.hint}>
            <div className="opp-bar-label">
              <span>{f.label}</span>
              <span className="opp-bar-weight">×{f.weight}%</span>
            </div>
            <div className="opp-bar-track">
              <div
                className="opp-bar-fill"
                style={{ width: f.value !== null ? `${Math.max(0, Math.min(100, f.value))}%` : '0%' }}
              />
            </div>
            <div className="opp-bar-value">{f.value !== null ? Math.round(f.value) : '—'}</div>
          </div>
        ))}
      </div>
      )}
      <dl className="drawer-attrs" style={{ marginTop: 12 }}>
        {data.opp_price_m2 !== null && (
          <Attr label="$/m² listado">
            <strong>{Math.round(data.opp_price_m2).toLocaleString('es-MX')}</strong>
          </Attr>
        )}
        {data.opp_zone_avg_price_m2 !== null && (
          <Attr label="$/m² zona (avg)" title={`Promedio del municipio basado en ${data.opp_zone_n_comparables ?? '?'} propiedades del mismo tipo.`}>
            {Math.round(data.opp_zone_avg_price_m2).toLocaleString('es-MX')}
            {data.opp_zone_n_comparables !== null && (
              <span className="crime-baseline"> · {data.opp_zone_n_comparables} comparables</span>
            )}
          </Attr>
        )}
        {data.opp_price_m2_zscore !== null && (
          <Attr label="z-score precio" title="Desviaciones estándar respecto al promedio del municipio. -2 = mucho más barato, +2 = mucho más caro.">
            <strong className={data.opp_price_m2_zscore < -0.5 ? 'crime-ok' : data.opp_price_m2_zscore > 0.5 ? 'crime-danger' : ''}>
              {data.opp_price_m2_zscore > 0 ? '+' : ''}{data.opp_price_m2_zscore.toFixed(2)}σ
            </strong>
          </Attr>
        )}
        {data.opp_crime_yoy_pct !== null && (
          <Attr label="Crimen total YoY">
            <YoyBadge value={data.opp_crime_yoy_pct} />
          </Attr>
        )}
      </dl>
      <div className="crime-footer">
        Metodología: ponderación 27/21/15/14/6/7/10 (precio / seguridad / completitud / legal / liquidez / riesgo físico / nivel socioeconómico). Si falta el precio comparable o el nivel socioeconómico, su peso se reparte entre los demás factores. Se recalcula con cada corrida del pipeline.
      </div>
    </>
  );
}

/**
 * Formatea el rango YYYYMM-YYYYMM como "oct 2024 → sep 2025". Ambos
 * son ints (no strings). Devuelve `null` si los datos están vacíos
 * (ej. MV sin REFRESH).
 */
function formatCrimePeriod(yyyymmMin: number, yyyymmMax: number): string | null {
  const fmt = (yyyymm: number): string | null => {
    if (!yyyymm || yyyymm < 100000) return null;
    const year = Math.floor(yyyymm / 100);
    const month = yyyymm % 100;
    if (month < 1 || month > 12) return null;
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${months[month - 1]} ${year}`;
  };
  const a = fmt(yyyymmMin);
  const b = fmt(yyyymmMax);
  if (!a || !b) return null;
  return `${a} → ${b}`;
}

/**
 * Controles de "observación" del drawer (RFC-0020): toggle de watchlist +
 * editor de nota. Solo visible con sesión (modelo aditivo). La nota se
 * persiste por usuario; "Guardar" llama a setNote (crea o actualiza).
 */
function WatchControls({ propertyId }: { propertyId: string }) {
  const { user, isWatching, toggleWatch, setNote, watchItems } = useAuth();
  const watching = isWatching(propertyId);
  const savedNote = watchItems.find((w) => w.id === propertyId)?.note ?? '';
  const [note, setNoteLocal] = useState(savedNote);
  const [savedFlag, setSavedFlag] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setNoteLocal(savedNote); }, [savedNote, propertyId]);

  if (!user) return null;

  async function onToggle() {
    setBusy(true);
    try { await toggleWatch(propertyId, note.trim() || undefined); }
    finally { setBusy(false); }
  }

  async function onSaveNote() {
    setBusy(true);
    try {
      await setNote(propertyId, note.trim());
      setSavedFlag(true);
      window.setTimeout(() => setSavedFlag(false), 1500);
    } finally { setBusy(false); }
  }

  return (
    <div className="watch-controls">
      <button
        type="button"
        className={`watch-toggle ${watching ? 'watching' : ''}`}
        onClick={() => void onToggle()}
        disabled={busy}
      >
        {watching ? '★ En observación' : '☆ Observar'}
      </button>
      {watching && (
        <div className="watch-note-edit">
          <textarea
            placeholder="Nota privada sobre esta propiedad…"
            value={note}
            onChange={(e) => setNoteLocal(e.target.value)}
            rows={2}
            maxLength={2000}
          />
          <button type="button" className="watch-note-save" onClick={() => void onSaveNote()} disabled={busy || note.trim() === savedNote.trim()}>
            {savedFlag ? '✓ Guardada' : 'Guardar nota'}
          </button>
        </div>
      )}
    </div>
  );
}

/** Grado de rezago → nivel socioeconómico legible (escala invertida). */
const NIVEL_SOCIO: Record<GradoRezago, string> = {
  'Muy bajo': 'alto',
  'Bajo': 'medio-alto',
  'Medio': 'medio',
  'Alto': 'medio-bajo',
  'Muy alto': 'bajo',
};

function numDash(v: number | null | undefined, suffix = ''): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toFixed(1)}${suffix}`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="drawer-section">
      <h3>{title}</h3>
      <dl className="drawer-attrs">{children}</dl>
    </section>
  );
}

function Tag({ children, accent, title }: { children: React.ReactNode; accent?: boolean; title?: string }) {
  return <span className={`tag ${accent ? 'tag-accent' : ''}`} title={title}>{children}</span>;
}

function Attr({ label, children, title }: { label: string; children: React.ReactNode; title?: string }) {
  return (
    <div className="attr" title={title}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * Chip con texto truncado + botón copiar al clipboard.
 * Mantiene el estado "idle/ok" 1.5 s para feedback visual.
 */
function CopyChip({
  text,
  short,
  onCopied,
  state,
  setState,
}: {
  text: string;
  short: string;
  onCopied: () => void;
  state: 'idle' | 'ok';
  setState: (s: 'idle' | 'ok') => void;
}) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      onCopied();
      setTimeout(() => setState('idle'), 1500);
    } catch {
      // clipboard puede fallar en contextos no-seguros; aceptable.
    }
  };
  return (
    <span className="copy-chip" title={text}>
      <code>{short}</code>
      <button onClick={handleCopy} className="copy-btn" aria-label="Copiar">
        {state === 'ok' ? '✓' : '⧉'}
      </button>
    </span>
  );
}

// ─── Historial de precio (RFC-0019) ──────────────────────────────

/** Solo mostramos la sección si hay al menos un cambio real (no baseline). */
function hasPriceChanges(data: PD): boolean {
  return (data.price_history ?? []).some((h) => h.previousPrice !== null);
}

function PriceHistoryList({ history, currency }: {
  history: NonNullable<PD['price_history']>;
  currency: string;
}) {
  // Más reciente primero (el API ya ordena DESC, pero no dependemos).
  const rows = [...history].sort(
    (a, b) => Date.parse(b.changedAt) - Date.parse(a.changedAt),
  );
  return (
    <div className="price-history">
      {rows.map((h, i) => {
        const price = Number(h.askingPrice);
        const prev = h.previousPrice !== null ? Number(h.previousPrice) : null;
        const pct = prev !== null && prev > 0 ? ((price - prev) / prev) * 100 : null;
        const drop = pct !== null && pct < 0;
        return (
          <div className="price-history-row" key={`${h.changedAt}-${i}`}>
            <span className="ph-date">{fmtDate(h.changedAt) ?? '—'}</span>
            <span className="ph-price">{fmtMoney(price, currency)}</span>
            {pct !== null ? (
              <span className={`ph-delta ${drop ? 'ph-down' : 'ph-up'}`}>
                {drop ? '↓' : '↑'} {Math.abs(pct).toFixed(1)}%
              </span>
            ) : (
              <span className="ph-delta ph-baseline">precio inicial</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────

/** Render `—` cuando el valor es null/undefined/string vacía. */
function dash(v: unknown): React.ReactNode {
  if (v === null || v === undefined) return <span className="dash">—</span>;
  if (typeof v === 'string' && v.trim() === '') return <span className="dash">—</span>;
  return v as React.ReactNode;
}

/** Variante para booleans: Sí / No / —. */
function boolDash(v: boolean | null | undefined): React.ReactNode {
  if (v === null || v === undefined) return <span className="dash">—</span>;
  return v ? <span className="bool-yes">Sí</span> : <span className="bool-no">No</span>;
}

function fmtMoney(n: number, currency: string): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency || 'MXN',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtDate(s: string | null | undefined): string | null {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: '2-digit' });
}

/** Versión corta del UUID (primeros 8 chars) para mostrar como chip. */
function shortId(id: string): string {
  return id.slice(0, 8);
}

/**
 * Parsea coords_geojson (string GeoJSON Point) a {lat,lng} sin pegar
 * a la lib turf, para mantener el bundle ligero.
 *
 * Ejemplo input: '{"type":"Point","coordinates":[-99.1283,19.6868]}'
 */
function parseCoords(s: string | null): { lat: number; lng: number } | null {
  if (!s) return null;
  try {
    const j = JSON.parse(s) as { type?: string; coordinates?: [number, number] };
    if (j?.type === 'Point' && Array.isArray(j.coordinates) && j.coordinates.length >= 2) {
      const [lng, lat] = j.coordinates;
      if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
  } catch {
    // intencional: input malformado → null.
  }
  return null;
}
